import React from 'react';
import I18n from 'i18n-js';
import {
  core_apimessages_Channel,
  core_apimessages_NegotiationAction as NegotiationAction,
  core_apimessages_NegotiationParty,
} from '../../schemas/rql';
import UpdateOfferModal from './update_offer_modal';
import UpdateOfferModalDialog from './update_offer_modal_dialog';
import classNames from 'classnames';
import { IUserContext, withUserContext } from '@reverbdotcom/commons/src/components/user_context_provider';
import { refreshDashboard } from './refresh_dashboard';
import { isExperimentEnabled } from '@reverbdotcom/commons/src/user_context_helpers';
import experiments from '@reverbdotcom/commons/src/experiments';
import { RCToast } from '@reverbdotcom/cadence/components';
import { MParticleEventName, trackEvent } from '@reverbdotcom/commons/src/elog/mparticle_tracker';
import { formatExperimentsForEventAttributes } from '@reverbdotcom/commons/src/elog/mparticle';

interface IExternalProps {
  listingId: string;
  isSeller: boolean;
  negotiationId: string;
  negotiationAction: NegotiationAction;
}

type IProps = IUserContext & IExternalProps;

export function handleSuccess(requestingParty: core_apimessages_NegotiationParty, negotiationId: string) {
  const updateOfferMessage = I18n.t('discovery.offers.updateSubmissionSuccess');
  refreshDashboard(negotiationId, updateOfferMessage, requestingParty);
}

export function UpdateOfferButton(props: IProps) {
  const [openDialog, setOpenDialog] = React.useState(false);
  const [confirmationToastText, setConfirmationToastText] = React.useState('');

  const inModalDialogExp = isExperimentEnabled(props.user, experiments.OFFER_MODAL_DIALOG_CONVERSION);
  const useDesignChanges = props.isSeller && isExperimentEnabled(props.user, experiments.UPDATE_OFFER_MODAL_DESIGN_UPDATES);

  React.useEffect(() => {
    if (openDialog) {
      trackEvent({
        eventName: MParticleEventName.ClickedUpdateOfferButton,
        listingId: props.listingId,
        experiments: formatExperimentsForEventAttributes(props.user, [experiments.UPDATE_OFFER_MODAL_DESIGN_UPDATES]),
        negotiationAction: props.negotiationAction,
        role: props.isSeller ? 'seller' : 'buyer',
      });
    }
  }, [openDialog]);

  return (
    <div>
      <button
        className={classNames(
          'offer-action', {
            'offer-action--accept': props.negotiationAction === NegotiationAction.ACCEPT,
            'offer-action--reject': props.negotiationAction === NegotiationAction.REJECT,
            'offer-action--counter': props.negotiationAction === NegotiationAction.COUNTER,
          })}
        onClick={() => {
          setOpenDialog(!openDialog);
        }}
        type="button"
      >
        {I18n.t(`discovery.offers.action.${props.negotiationAction.toLocaleLowerCase()}`)}
      </button>

      {!inModalDialogExp && (
        <UpdateOfferModal
          onRequestDialogClose={() => { setOpenDialog(false); }}
          isDialogOpen={openDialog}
          isSeller={props.isSeller}
          onSuccess={(party) => { handleSuccess(party, props.negotiationId); }}
          listingId={props.listingId}
          negotiationId={props.negotiationId}
          negotiationAction={props.negotiationAction}
          shopChannel={core_apimessages_Channel.REVERB}
        />
      )}

      {inModalDialogExp && (
        <>
          <UpdateOfferModalDialog
            onRequestDialogClose={() => { setOpenDialog(false); }}
            isDialogOpen={openDialog}
            isSeller={props.isSeller}
            onSuccess={(party) => {
              handleSuccess(party, props.negotiationId);

              if (useDesignChanges && props.negotiationAction === NegotiationAction.REJECT) {
                setConfirmationToastText(I18n.t('discovery.offers.toast.rejected'));
              }
            }}
            listingId={props.listingId}
            negotiationId={props.negotiationId}
            negotiationAction={props.negotiationAction}
            useDesignChanges={useDesignChanges}
          />
          <RCToast
            isOpen={!!confirmationToastText}
            onClose={() => { setConfirmationToastText(''); }}
            text={confirmationToastText}
            theme="success"
          />
        </>
      )}
    </div>
  );
}

export default withUserContext<IExternalProps>(UpdateOfferButton);
